import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  acceptOrganizationInvitation,
  type AcceptInvitationResult,
} from "@/lib/organization-client";

type ViewState =
  | { status: "idle" }
  | { status: "accepting" }
  | { status: "error"; message: string }
  | { status: "missing" };

function buildLoginRedirect(invitationId: string): string {
  const target = `/accept-invitation?id=${encodeURIComponent(invitationId)}`;
  return `/login?redirect=${encodeURIComponent(target)}`;
}

export default function AcceptInvitationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get("id")?.trim() ?? "";
  const [view, setView] = useState<ViewState>(
    invitationId ? { status: "idle" } : { status: "missing" },
  );

  const runAccept = useCallback(async (): Promise<void> => {
    if (!invitationId) {
      setView({ status: "missing" });
      return;
    }
    setView({ status: "accepting" });
    const result: AcceptInvitationResult = await acceptOrganizationInvitation(invitationId);
    if (result.kind === "success") {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (result.kind === "unauthorized") {
      navigate(buildLoginRedirect(invitationId), { replace: true });
      return;
    }
    setView({ status: "error", message: result.message });
  }, [invitationId, navigate]);

  useEffect(() => {
    if (!invitationId) {
      return;
    }
    void runAccept();
  }, [invitationId, runAccept]);

  if (view.status === "missing") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <h1 className="text-2xl font-bold text-foreground">Invitation not found</h1>
          <p className="text-sm text-muted-foreground">
            This invitation link is missing an invitation id. Please check the link in your email.
          </p>
          <Link
            to="/login"
            className="inline-block text-sm font-medium text-primary hover:underline"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (view.status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <h1 className="text-2xl font-bold text-foreground">We couldn't accept your invitation</h1>
          <p className="text-sm text-muted-foreground" data-testid="accept-invitation-error">
            {view.message}
          </p>
          <button
            type="button"
            onClick={() => {
              void runAccept();
            }}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <Link
            to="/dashboard"
            className="inline-block text-sm font-medium text-primary hover:underline"
          >
            Go to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-4 text-center">
        <h1 className="text-2xl font-bold text-foreground">Accepting your invitation…</h1>
        <p className="text-sm text-muted-foreground">
          Hang tight while we add you to the organization.
        </p>
      </div>
    </div>
  );
}
